import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  StockAdjustmentInput,
  StockMovementQueryInput,
  StockThresholdUpdateInput,
} from "@/modules/inventory/domain/contracts";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { inventoryApi } from "./client";

function normalizeFiltersFromSearch(
  filters: OffsetPageQueryDTO["filters"],
): FilterConditionDTO[] | undefined {
  if (filters == null) return undefined;
  if (Array.isArray(filters)) return filters;
  if (typeof filters === "string") {
    try {
      const parsed = JSON.parse(filters) as unknown;
      return Array.isArray(parsed)
        ? (parsed as FilterConditionDTO[])
        : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export const inventoryKeys = {
  all: ["inventory"] as const,
  stock: {
    all: ["inventory", "stock"] as const,
    list: (q: Partial<OffsetPageQueryDTO>) =>
      ["inventory", "stock", "list", q] as const,
    cursorList: (params: {
      filters: FilterConditionDTO[] | undefined;
      limit: number;
    }) => ["inventory", "stock", "cursor-list", params] as const,
    productsCursorList: (params: {
      filters: FilterConditionDTO[] | undefined;
      limit: number;
    }) => ["inventory", "stock", "products-cursor-list", params] as const,
    summary: (filters: OffsetPageQueryDTO["filters"] | undefined) =>
      ["inventory", "stock", "summary", filters] as const,
    detail: (variantId: string) =>
      ["inventory", "stock", "detail", variantId] as const,
  },
  movements: {
    all: ["inventory", "movements"] as const,
    list: (q: Partial<StockMovementQueryInput>) =>
      ["inventory", "movements", "list", q] as const,
  },
};

export function useStockListQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: normalizeFiltersFromSearch(query.filters),
  };
  return useQuery({
    queryKey: inventoryKeys.stock.list(q),
    queryFn: () => inventoryApi.stock.list(q),
  });
}

export function useStockSummaryQuery(
  filters: OffsetPageQueryDTO["filters"] | undefined,
) {
  const normalized = normalizeFiltersFromSearch(filters);
  return useQuery({
    queryKey: inventoryKeys.stock.summary(normalized),
    queryFn: () => inventoryApi.stock.summary({ filters: normalized }),
  });
}

export function useStockQuery(variantId: string) {
  return useQuery({
    queryKey: inventoryKeys.stock.detail(variantId),
    queryFn: () => inventoryApi.stock.get(variantId),
    enabled: !!variantId,
  });
}

export function useAdjustStockMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StockAdjustmentInput) =>
      inventoryApi.stock.adjust(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inventoryKeys.stock.all });
      toast.success("ປັບສະຕ໋ອກສໍາເລັດ");
    },
  });
}

export function useUpdateThresholdMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StockThresholdUpdateInput & { variantId: string }) =>
      inventoryApi.stock.updateThreshold(input.variantId, input),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: inventoryKeys.stock.all });
      qc.invalidateQueries({
        queryKey: inventoryKeys.stock.detail(variables.variantId),
      });
      toast.success("ອັບເດດຂີດຈຳກັດສະຕ໋ອກສໍາເລັດ");
    },
  });
}

export function useStockMovementsQuery(
  query: Partial<StockMovementQueryInput> = {},
) {
  const q: StockMovementQueryInput = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    variantId: query.variantId,
    type: query.type,
    dateFrom: query.dateFrom,
    dateTo: query.dateTo,
  };
  return useQuery({
    queryKey: inventoryKeys.movements.list(q),
    queryFn: () => inventoryApi.movements.list(q),
  });
}
